import * as _ from "lodash";

/**
 * Checks if string starts with the given target string.
 *
 * @param string The string to search.
 * @param target The string to search for.
 * @param position The position to search from.
 * @return Returns true if string starts with target, else false.
 */
function startsWith(string?: string, target?: string, position?: number): boolean {
    return _.startsWith(string, target, position)
};

/**
 * Removes leading and trailing whitespace or specified characters from string.
 *
 * @param string The string to trim.
 * @param chars The characters to trim.
 * @return Returns the trimmed string.
 */
function trim(string?: string, chars?: string): string;
/**
 * @see _.trim
 */
function trim(string: string, index: string | number, guard: object): string;

function trim(string, ...args) {
    return _.trim(string, ...args)
}

/**
 * escapes special characters in a string
 * @param string The string to escape
 * @return Returns the escaped string
 * @example
 * escape("<div>") // returns "&lt;div&gt;"
 * escape("&") // returns "&amp;"
 * escape("'") // returns "&#39;"
 * escape('"') // returns "&quot;"
 */
function escape(string: string): string {
    return _.escape(string);
}

/**
 * Generates unique IDs
 * @return Returns the unique ID
 */
function uniqueId(string?: string): string {
    return _.uniqueId(string);
}

/**
 * Converts the value to a string
 * @param value The value to convert
 * @return Returns the converted string
 * @example
 * toString(null) // returns ""
 * toString(-0) // returns "-0"
 * toString([1, 2, 3]) // returns "1,2,3"
 * 
 */
function toString(value: any): string {
    return _.toString(value);
}

/**
 * Converts the string, as space separated words, to upper case
 * @param string The string to convert
 * @return Returns the converted string
 * @example
 * upperCase('--foo-bar') // returns 'FOO BAR'
 * upperCase('fooBar') // returns 'FOO BAR'
 */
function upperCase(string: string): string {
    return _.upperCase(string);
}

/**
 * Converts the string, as a whole, to lower case
 * @param string The string to convert
 * @return Returns the converted string
 * @example
 * toLower('--Foo-Bar') // returns '--foo-bar'
 * toLower('fooBar') // returns 'foobar'
 * toLower('__FOO_BAR__') // returns '__foo_bar__'
 */
function toLower(string?: string): string {
    return _.toLower(string);
}

/** Determines if string ends with the given target string 
 * @param string The string to search
 * @param target The string to search for
 * @return Returns true if string ends with target, else false
 * @example
 * endsWith('abc', 'c') // returns true
 * endsWith('abc', 'b') // returns false
 * endsWith('', 'b') // returns false
*/
function endsWith(string?: string, target?: string, position?: number): boolean {
    return _.endsWith(string, target, position);
}

/** Replaces all occurrences of a string in another string
 * @param string The string to modify
 * @param pattern The pattern to replace
 * @param replacement The string to replace the pattern
 * @return Returns the modified string
 * @example
 * replace('abc', 'b', 'd') // returns 'adc'
 * replace('abc', 'b', '') // returns 'ac'
 * replace('abc', 'd', 'e') // returns 'abc'
*/

function replace(string?: string, pattern?: string | RegExp, replacement?: string): string;
function replace(string, pattern, replacement) {
    return _.replace(string, pattern, replacement);
}

/** Removes leading whitespace or specified characters from string
 * @param string The string to trim
 * @param chars The characters to trim
 * @return Returns the trimmed string
 * @example
 * trimStart('  abc  ') // returns 'abc  '
 * trimStart('-_-abc-_-', '_-') // returns 'abc-_-'
*/
function trimStart(string?: string, chars?: string): string;
function trimStart(string, ...args) {
    return _.trimStart(string, ...args);
}

/**
 * Converts the string to start case
 * @param string The string to convert
 * @return Returns the converted string
 * @example
 * startCase('--foo-bar') // returns 'Foo Bar'
 * startCase('fooBar') // returns 'Foo Bar'
 */
function startCase(string: string): string {
    return _.startCase(string);
}

/** Converts the first letter of a string to lowercase
 * @param string The string to convert
 * @return Returns the converted string
 * @example
 * lowerFirst('Fred') // returns 'fred'
 * lowerFirst('FRED') // returns 'fRED'
 * lowerFirst('__FRED__') // returns '__fRED__'
 */
function lowerFirst(string: string): string {
    return _.lowerFirst(string);
}

/** Converts the first letter of a string to uppercase
 * @param string The string to convert
 * @return Returns the converted string
 * @example
 * upperFirst('fred') // returns 'Fred'
 * upperFirst('FRED') // returns 'FRED'
 * upperFirst('__fred__') // returns '__fred__'
 */
function upperFirst(string: string): string {
    return _.upperFirst(string);
}

/** Repeats the given string n times
 * @param string The string to repeat
 * @param n The number of times to repeat the string
 * @return Returns the repeated string
 * @example
 * repeat('*', 3) // returns '***'
 * repeat('abc', 2) // returns 'abcabc'
 * repeat('abc', 0) // returns ''
 */
function repeat(string: string, n: number): string {
    return _.repeat(string, n);
}

// var deburrLetter = require('./_deburrLetter');

/** Used to match Latin Unicode letters (excluding mathematical operators). */
// var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

/** Used to compose unicode character classes. */
// var rsComboMarksRange = '\\u0300-\\u036f',
//     reComboHalfMarksRange = '\\ufe20-\\ufe2f',
//     rsComboSymbolsRange = '\\u20d0-\\u20ff',
//     rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange;

/** Used to compose unicode capture groups. */
// var rsCombo = '[' + rsComboRange + ']';

/**
 * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
 * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
 */
// var reComboMark = RegExp(rsCombo, 'g');

/**
 * Deburrs `string` by converting
 * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
 * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
 * letters to basic Latin letters and removing
 * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to deburr.
 * @returns {string} Returns the deburred string.
 * @example
 *
 * _.deburr('déjà vu');
 * // => 'deja vu'
 */
// function deburr(string) {
//   string = toString(string);
//   return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
// }

function deburr(string: string): string {
    return _.deburr(string);
}

/**
 * Escapes the RegExp special characters "^", "$", "\", ".", "*", "+", "?", "(", ")", "[", "]",
 * "{", "}", and "|" in string.
 *
 * @param string The string to escape.
 * @return Returns the escaped string.
 */
function escapeRegExp(string?: string): string {
    return _.escapeRegExp(string);
}

export const StringUtil = { 
    startsWith,
    trim,
    escape,
    uniqueId,
    toString,
    upperCase,
    toLower,
    endsWith,
    replace,
    trimStart,
    startCase,
    lowerFirst,
    upperFirst,
    repeat,
    deburr,
    escapeRegExp
};

export default StringUtil;